import { useEffect, useState, useCallback, useRef } from 'react';
import { Box, IconButton, styled, Typography } from '@mui/material';
import GenericDraggableTableView from '../../../../Features/Table/GenericDraggableTable';
import CustomIcon from '../../../../Components/CustomIcon';
import { useTranslation } from 'react-i18next';
import { getColumnsConfig } from '../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations';
import { MAX_COLUMN_SELECTION_COUNT } from '../../../../Helper/DigitalTwin/DataFile';
import { usePagesContent } from '../../../../Hooks/usePagesContent';
import { EventDetailsTableColumns } from './Data/EventDetailsTableColumns';
import { groupEventDetailsData } from './CommonFunctions';
import { fetchFiltersDataObject } from '../../../../Helper/Table/TableHelper';
import { EventDetailsTableFilters } from './Data/EventDetailsTableFilters';
import { fleetEnergyEventsAction, getFleetEnergyEvents } from '../../../../Api/PerformanceApi';
import ProgressBarColumnView from '../../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/ProgressBarColumnView';
import { getSOHColor } from '../../../../Helper/BatteryHealth/BatteryHealthHelper';

const TableContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    minHeight: '200px',
    '& .ant-table-cell-with-append': {
        display: 'flex',
        alignItems: 'center'
    }
}))

const customRenderers = {
    event_group: (record, config) => {
        const isDischarge = record?.eventType?.toLowerCase() === 'discharging';
        return (
            <Box sx={{ backgroundColor: isDischarge ? '#FFF2F2' : 'transparent', padding: '0 4px' }}>
                <Typography variant="fw400s14lh24c0061CA" sx={{ "color": "#3D4B5A !important" }}>
                    {record[config.dataIndex]}
                </Typography>
            </Box>
        );
    },
    'progress-bar': (record, config) => {
        const value = record?.[config?.dataIndex];
        const decimalPlaces = config?.decimalPlaces
        const barColor = getSOHColor(value)
        const startProgressFrom = config?.startProgressFrom ? config?.startProgressFrom : 0
        const unitPostFix = config?.unitPostfix ? config?.unitPostfix : ""
        const defaultValue = config?.defaultValue ? config?.defaultValue : "Not Available"
        return <ProgressBarColumnView
            minProgress={startProgressFrom}
            defaultText={defaultValue}
            value={value ? value?.toFixed(decimalPlaces) : 0}
            barColor={barColor}
            record={record}
            config={config}
            unitPostFix={unitPostFix}
        />
    }
};

const EventDetailsTable = ({
    batteryID,
    startDate,
    endDate
}) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const { t } = useTranslation(['common', 'generic_table']);
    const pageContent = usePagesContent();
    const [allTableColumns, setAllTableColumns] = useState([]);
    const [currentTableColumns, setCurrentTableColumns] = useState(allTableColumns);
    const [selectedTableColumns, setSelectedTableColumns] = useState(currentTableColumns.map(column => column.dataIndex));
    const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [filters, setFilters] = useState({});
    const [selectedFilters, setSelectedFilters] = useState({});
    const [filtersChanged, setFiltersChanged] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [eventsData, setEventsData] = useState([]);
    const prevFiltersRef = useRef(null);
    const prevDatesRef = useRef(null);
    const isInitialMount = useRef(true);

    // Helper to get base API parameters
    const getBaseListActionApiParams = useCallback(() => {
        const eventTypeColumn = allTableColumns.find((column) => column.dataIndex === 'eventType');
        const updatedColumnOrder = eventTypeColumn ? [eventTypeColumn.headerLabel, ...columnOrder] : columnOrder;
        const updatedColumns = eventTypeColumn ? [eventTypeColumn, ...currentTableColumns] : currentTableColumns;

        return {
            batteryID: batteryID,
            startDate: startDate,
            endDate: endDate,
            columnOrder: updatedColumnOrder,
            columns: JSON.stringify(updatedColumns),
            ...selectedFilters,
        };
    }, [batteryID, startDate, endDate, columnOrder, currentTableColumns, selectedFilters, allTableColumns]);

    // Fetch events data for the table
    const fetchEventsData = useCallback(async () => {
        try {
            setIsLoading(true);

            const fleetEventsResponse = await getFleetEnergyEvents({
                batteryID: [batteryID],
                startDate: startDate,
                endDate: endDate,
                skipPagination: true,
                ...selectedFilters,
            });
            const data = fleetEventsResponse?.response?.data ?? [];
            setEventsData(data);
        } catch (error) {
            console.error('Error fetching table events:', error);
            setEventsData([]);
        } finally {
            setIsLoading(false);
        }
    }, [batteryID, startDate, endDate, selectedFilters, filtersChanged]);

    const loadColumnData = useCallback(() => {
        const tableColumns = EventDetailsTableColumns.columnsConfig ?? [];
        const columnOrder = EventDetailsTableColumns.columnOrdering ?? [];

        const formattedColumns = tableColumns.reduce((acc, curr) => {
            acc[curr.dataIndex] = curr;
            return acc;
        }, {});

        // adding degree symbol for temperature
        const columnsWithReplacedKeys = Object.fromEntries(
            Object.entries(formattedColumns).map(([key, value]) => {
                return [
                    key,
                    {
                        ...value,
                        title: value.title.replace(/\(\s*C\s*\)/g, "(°C)"),
                    }
                ]
            })
        );

        const columnsConfig = getColumnsConfig({
            tableColumns: columnsWithReplacedKeys,
            tableColumnOrder: columnOrder,
            customRenderers,
            t
        });

        // remove duplicates
        let uniqueColumnsConfig = columnsConfig.filter((item, index, self) =>
            index === self.findIndex((t) => t.dataIndex === item.dataIndex)
        );

        // sort by fixed key and then pick the first MAX_COLUMN_SELECTION_COUNT columns
        const defaultColumns = uniqueColumnsConfig.sort((a, b) => {
            if (a.fixed && !b.fixed) return -1;
            if (!a.fixed && b.fixed) return 1;
            return 0;
        }).slice(0, MAX_COLUMN_SELECTION_COUNT);

        setAllTableColumns(uniqueColumnsConfig);

        const filteredColumns = defaultColumns.filter((column) => column.dataIndex !== 'eventType');

        setCurrentTableColumns(filteredColumns);
        setSelectedTableColumns(filteredColumns.map(column => column.dataIndex));
        setDefaultSelectedColumns(filteredColumns.map(column => column.dataIndex));
        setColumnOrder(filteredColumns.map((column) => column.headerLabel));
    }, [t]);

    const onColumnOrderChange = (tableColumns) => {
        const updatedColumnOrder = tableColumns.map((value) => value.headerLabel);
        setColumnOrder(updatedColumnOrder);
    };

    const applyFilters = (args) => {
        setSelectedFilters(args);
        setFiltersChanged(filtersChanged + 1);
    }

    // Initialize columns configuration
    useEffect(() => {
        loadColumnData();
    }, [loadColumnData]);

    // Initialize filters
    useEffect(() => {
        const initializeFilters = async () => {
            // Load filters from EventDetailsTableFilters
            const eventDetailsFilters = EventDetailsTableFilters || [];

            if (!eventDetailsFilters || eventDetailsFilters.length === 0) {
                return;
            }

            const filtersObj = await fetchFiltersDataObject(eventDetailsFilters, {});

            const sortedFiltersObj = Object.fromEntries(
                Object.entries(filtersObj).sort(([, a], [, b]) => a.label.localeCompare(b.label))
            );

            setFilters(sortedFiltersObj);
        };

        initializeFilters();
    }, []);

    // Fetch data on mount and when dates or filters change
    useEffect(() => {
        if (batteryID && startDate && endDate) {
            // On initial mount, always fetch
            if (isInitialMount.current) {
                isInitialMount.current = false;
                prevFiltersRef.current = selectedFilters;
                prevDatesRef.current = { startDate, endDate };
                fetchEventsData();
                return;
            }

            // On subsequent updates, check if filters or dates changed
            // const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(selectedFilters);
            // console.log(filtersChanged);
            const datesChanged = prevDatesRef.current?.startDate !== startDate || prevDatesRef.current?.endDate !== endDate;

            if (filtersChanged || datesChanged) {
                prevFiltersRef.current = selectedFilters;
                prevDatesRef.current = { startDate, endDate };
                fetchEventsData();
            }
        }
    }, [batteryID, startDate, endDate, selectedFilters, fetchEventsData, filtersChanged]);

    // Update table data when events data changes
    useEffect(() => {
        if (!isLoading && eventsData.length > 0) {
            const groupedData = groupEventDetailsData(eventsData);
            setTableData(groupedData);

            // Expand the first row by default if it has children
            if (groupedData.length > 0 && groupedData[0].children && groupedData[0].children.length > 0) {
                setExpandedRowKeys([groupedData[0].key]);
            }
        } else if (!isLoading && eventsData.length === 0) {
            setTableData([]);
            setExpandedRowKeys([]);
        }
    }, [eventsData, isLoading]);

    const updateSelectedTableColumns = (newSelectedColumns) => {
        const filteredColumns = allTableColumns.filter((columnConfig) => {
            return newSelectedColumns.includes(columnConfig.dataIndex)
        });
        setCurrentTableColumns(filteredColumns);
        setSelectedTableColumns(newSelectedColumns);
        setColumnOrder(filteredColumns.map((col) => col.headerLabel || col.title));
    }

    const getCSVFileName = () => {
        return `BatteryPerformance_${batteryID}_${new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })}.csv`;
    };

    const getCsvColumnParams = () => {
        const columnsForDownload = currentTableColumns.filter(column => column.key !== 'event_group');
        const columnOrderForDownload = columnOrder.filter(column => column !== 'Group');

        const eventTypeColumn = allTableColumns.find((column) => column.dataIndex === 'eventType');
        const updatedColumns = eventTypeColumn ? [eventTypeColumn, ...columnsForDownload] : columnsForDownload;
        const updatedColumnOrder = eventTypeColumn ? [eventTypeColumn.headerLabel, ...columnOrderForDownload] : columnOrderForDownload;

        return {
            columns: JSON.stringify(updatedColumns),
            columnOrder: updatedColumnOrder,
        };
    };

    const getCSVData = async () => {
        const res = await fleetEnergyEventsAction({
            actionType: "download-csv",
            ...getBaseListActionApiParams(),
        })
        const csvData = res?.response?.data || [];
        return csvData;
    }

    const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
        await fleetEnergyEventsAction({
            actionType: "mail",
            ...getBaseListActionApiParams(),
            emailTo: sendTo,
            emailCc: sendCc,
            subject: subject,
            body: body,
        })

        return [];
    };

    const onExpand = (expanded, record) => {
        const keys = [...expandedRowKeys];
        if (expanded) {
            keys.push(record.key);
        } else {
            const index = keys.indexOf(record.key);
            if (index > -1) {
                keys.splice(index, 1);
            }
        }
        setExpandedRowKeys(keys);
    };

    const ExpandIconComponent = ({ expanded, onExpand, record }) => {
        if (!record.children || record.children.length === 0) {
            return null
        }
        return (
            <IconButton
                disableRipple
                disableTouchRipple
                disableFocusRipple
                sx={{
                    fontSize: '16px', paddingLeft: 0, backgroundColor: 'transparent !important',
                    '&:hover': { backgroundColor: 'transparent !important' }
                }} onClick={e => onExpand(record, e)}>
                <CustomIcon name="primary-arrow-icon" rotate={expanded ? 90 : 0} />
            </IconButton >
        )
    }

    return (
        <TableContainer>
            <GenericDraggableTableView
                tableID={'EventDetailsTable'}
                title={t('digital_twin:event_details', 'Event Details')}
                containerStyles={{ width: 'auto' }}
                disableSearch={true}
                getCSVData={getCSVData}
                getCSVFileName={getCSVFileName}
                getCsvColumnSettings={getCsvColumnParams}
                onSendEmail={onSendEmail}
                alwaysEnableActions={true}
                availableEmailDomains={pageContent?.metaInfo?.availableEmailDomains || ["coulomb.ai"]}
                showPrimaryButton={false}
                tableColumns={currentTableColumns}
                onColumnOrderChange={onColumnOrderChange}
                columnOrder={columnOrder}
                tableData={tableData}
                isLoading={isLoading}
                disablePagination={true}
                emptyMessage={t('common:no_data_found', 'No data found')}
                customRightActions={true}
                expandable={{
                    expandedRowKeys,
                    onExpand,
                    expandIcon: ExpandIconComponent,
                }}
                initialColumns={allTableColumns}
                selectedColumns={selectedTableColumns}
                applySelectedColumns={updateSelectedTableColumns}
                filters={filters}
                selectedFilters={selectedFilters}
                applyFilters={(args) => { applyFilters(args); console.log(args) }}
                maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
                defaultSelectedColumns={defaultSelectedColumns}
            />
        </TableContainer>
    );
};
export default EventDetailsTable;
